import { useState, useRef, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { aiAPI } from '../../lib/api';
import { 
  Microphone, 
  Stop, 
  Play,
  Pause,
  Trash,
  ClipboardText,
  Check,
  Warning,
  ArrowClockwise,
  Download,
  Copy
} from '@phosphor-icons/react';
import { toast } from 'sonner';

export default function AIScribePage() {
  const { user } = useAuth();
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [recordingTime, setRecordingTime] = useState(0);
  const [transcription, setTranscription] = useState('');
  const [clinicalNotes, setClinicalNotes] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingStep, setProcessingStep] = useState('');
  
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerRef = useRef(null);

  useEffect(() => {
    return () => {
      // Cleanup on unmount
      if (timerRef.current) clearInterval(timerRef.current);
      if (audioUrl) URL.revokeObjectURL(audioUrl);
    };
  }, [audioUrl]);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      
      mediaRecorderRef.current = new MediaRecorder(stream, {
        mimeType: 'audio/webm;codecs=opus'
      });
      
      audioChunksRef.current = [];
      
      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };
      
      mediaRecorderRef.current.onstop = () => {
        const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        setAudioBlob(blob);
        setAudioUrl(URL.createObjectURL(blob));
        
        // Stop all tracks
        stream.getTracks().forEach(track => track.stop());
      };
      
      mediaRecorderRef.current.start(1000); // Collect data every second
      setIsRecording(true);
      setRecordingTime(0);
      
      // Start timer
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
      
      toast.success('Recording started');
    } catch (error) {
      process.env.NODE_ENV === "development" && console.error('Error starting recording:', error);
      toast.error('Could not access microphone. Please check permissions.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      setIsPaused(false);
      
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      
      toast.success('Recording stopped');
    }
  };

  const pauseRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      if (isPaused) {
        mediaRecorderRef.current.resume();
        timerRef.current = setInterval(() => {
          setRecordingTime(prev => prev + 1);
        }, 1000);
        setIsPaused(false);
      } else {
        mediaRecorderRef.current.pause();
        if (timerRef.current) {
          clearInterval(timerRef.current);
        }
        setIsPaused(true);
      }
    }
  };

  const clearRecording = () => {
    if (audioUrl) URL.revokeObjectURL(audioUrl);
    setAudioBlob(null);
    setAudioUrl(null);
    setRecordingTime(0);
    setTranscription('');
    setClinicalNotes('');
    toast.success('Recording cleared');
  };

  const processRecording = async () => {
    if (!audioBlob) {
      toast.error('No recording to process');
      return;
    }

    setIsProcessing(true);
    setProcessingStep('Converting audio...');

    try {
      // Convert blob to base64
      const reader = new FileReader();
      const base64Promise = new Promise((resolve, reject) => {
        reader.onloadend = () => {
          const base64 = reader.result.split(',')[1];
          resolve(base64);
        };
        reader.onerror = reject;
      });
      reader.readAsDataURL(audioBlob);
      
      const base64Audio = await base64Promise;
      
      setProcessingStep('Transcribing audio with AI...');
      
      // Send to transcription endpoint
      const response = await aiAPI.transcribe({
        audio_data: base64Audio,
        generate_notes: true
      });
      
      setTranscription(response.data.transcription || '');
      setClinicalNotes(response.data.clinical_notes || '');
      
      toast.success('Transcription complete!');
    } catch (error) {
      process.env.NODE_ENV === "development" && console.error('Processing error:', error);
      toast.error(error.response?.data?.detail || 'Failed to process recording');
    } finally {
      setIsProcessing(false);
      setProcessingStep('');
    }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard`);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="p-8" data-testid="ai-scribe-page">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-blue-500 rounded-xl flex items-center justify-center">
            <Microphone size={24} className="text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope' }}>
              AI Medical Scribe
            </h1>
            <p className="text-slate-500">Record encounters and generate clinical documentation</p>
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-8">
        {/* Recording Section */}
        <div className="space-y-6">
          {/* Recording Card */}
          <div className="card-clinical p-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-4">Voice Recording</h2>
            
            {/* Recording Status */}
            <div className={`mb-6 p-6 rounded-xl text-center ${
              isRecording 
                ? 'bg-red-50 border-2 border-red-200' 
                : audioBlob 
                  ? 'bg-green-50 border-2 border-green-200'
                  : 'bg-slate-50 border-2 border-slate-200'
            }`}>
              {isRecording ? (
                <>
                  <div className="flex items-center justify-center gap-3 mb-4">
                    <div className={`w-4 h-4 rounded-full ${isPaused ? 'bg-amber-500' : 'bg-red-500 animate-pulse'}`} />
                    <span className="text-lg font-medium text-slate-900">
                      {isPaused ? 'Paused' : 'Recording...'}
                    </span>
                  </div>
                  <div className="text-4xl font-mono font-bold text-slate-900 mb-4">
                    {formatTime(recordingTime)}
                  </div>
                  <p className="text-sm text-slate-500">
                    Speak clearly about the patient encounter
                  </p>
                </>
              ) : audioBlob ? (
                <>
                  <Check size={48} className="text-green-500 mx-auto mb-2" />
                  <p className="text-lg font-medium text-slate-900 mb-2">Recording Complete</p>
                  <p className="text-sm text-slate-500">Duration: {formatTime(recordingTime)}</p>
                  {audioUrl && (
                    <audio controls src={audioUrl} className="mt-4 w-full" />
                  )}
                </>
              ) : (
                <>
                  <Microphone size={48} className="text-slate-400 mx-auto mb-2" />
                  <p className="text-lg font-medium text-slate-900 mb-2">Ready to Record</p>
                  <p className="text-sm text-slate-500">Click the button below to start recording</p>
                </>
              )}
            </div>

            {/* Recording Controls */}
            <div className="flex items-center justify-center gap-4">
              {!isRecording && !audioBlob && (
                <button
                  onClick={startRecording}
                  data-testid="start-recording-btn"
                  className="flex items-center gap-2 px-6 py-3 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors font-medium"
                >
                  <Microphone size={20} weight="fill" />
                  Start Recording
                </button>
              )}

              {isRecording && (
                <>
                  <button
                    onClick={pauseRecording}
                    className="flex items-center gap-2 px-4 py-3 bg-amber-500 text-white rounded-full hover:bg-amber-600 transition-colors"
                  >
                    {isPaused ? <Play size={20} weight="fill" /> : <Pause size={20} weight="fill" />}
                    {isPaused ? 'Resume' : 'Pause'}
                  </button>
                  <button
                    onClick={stopRecording}
                    data-testid="stop-recording-btn"
                    className="flex items-center gap-2 px-6 py-3 bg-slate-800 text-white rounded-full hover:bg-slate-900 transition-colors font-medium"
                  >
                    <Stop size={20} weight="fill" />
                    Stop Recording
                  </button>
                </>
              )}

              {audioBlob && !isRecording && (
                <>
                  <button
                    onClick={processRecording}
                    disabled={isProcessing}
                    data-testid="process-recording-btn"
                    className="flex items-center gap-2 px-6 py-3 bg-teal-600 text-white rounded-full hover:bg-teal-700 disabled:bg-teal-300 transition-colors font-medium"
                  >
                    {isProcessing ? (
                      <>
                        <ArrowClockwise size={20} className="animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <ClipboardText size={20} weight="fill" />
                        Generate Notes
                      </>
                    )}
                  </button>
                  <button
                    onClick={clearRecording}
                    className="flex items-center gap-2 px-4 py-3 bg-slate-200 text-slate-700 rounded-full hover:bg-slate-300 transition-colors"
                  >
                    <Trash size={20} />
                    Clear
                  </button>
                </>
              )}
            </div>

            {/* Processing Status */}
            {isProcessing && processingStep && (
              <div className="mt-4 p-4 bg-teal-50 rounded-xl border border-teal-200">
                <div className="flex items-center gap-3">
                  <ArrowClockwise size={20} className="text-teal-600 animate-spin" />
                  <span className="text-teal-700 font-medium">{processingStep}</span>
                </div>
              </div>
            )}

            {/* Instructions */}
            <div className="mt-6 p-4 bg-blue-50 rounded-xl border border-blue-200">
              <h3 className="font-medium text-blue-900 mb-2">How to Use</h3>
              <ol className="text-sm text-blue-700 space-y-1 list-decimal list-inside">
                <li>Click <strong>"Start Recording"</strong> when ready</li>
                <li>Describe the patient encounter clearly</li>
                <li>Click <strong>"Stop Recording"</strong> when finished</li>
                <li>Click <strong>"Generate Notes"</strong> to create documentation</li>
              </ol>
              <p className="text-xs text-blue-600 mt-3">
                <Warning size={14} className="inline mr-1" />
                Only record clinical information intended for the medical record.
              </p>
            </div>
          </div>
        </div>

        {/* Results Section */}
        <div className="space-y-6">
          {/* Transcription */}
          <div className="card-clinical p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-slate-900">Transcription</h2>
              {transcription && (
                <button
                  onClick={() => copyToClipboard(transcription, 'Transcription')}
                  className="p-2 text-slate-500 hover:text-teal-600 hover:bg-teal-50 rounded-lg transition-colors"
                >
                  <Copy size={18} />
                </button>
              )}
            </div>
            {transcription ? (
              <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 max-h-64 overflow-y-auto">
                <p className="text-slate-700 whitespace-pre-wrap">{transcription}</p>
              </div>
            ) : (
              <div className="p-8 bg-slate-50 rounded-xl border border-slate-200 text-center">
                <p className="text-slate-400">Transcription will appear here after processing</p>
              </div>
            )}
          </div>

          {/* Clinical Notes */}
          <div className="card-clinical p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-slate-900">Clinical Notes (SOAP)</h2>
              {clinicalNotes && (
                <button
                  onClick={() => copyToClipboard(clinicalNotes, 'Clinical notes')}
                  className="p-2 text-slate-500 hover:text-teal-600 hover:bg-teal-50 rounded-lg transition-colors"
                >
                  <Copy size={18} />
                </button>
              )}
            </div>
            {clinicalNotes ? (
              <div className="p-4 bg-green-50 rounded-xl border border-green-200 max-h-96 overflow-y-auto">
                <p className="text-slate-700 whitespace-pre-wrap">{clinicalNotes}</p>
              </div>
            ) : (
              <div className="p-8 bg-slate-50 rounded-xl border border-slate-200 text-center">
                <p className="text-slate-400">AI-generated clinical notes will appear here</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
